import React from "react";
import { ViewSortDirection } from "../hooks/useTableViewSort";

interface TableSortThProps {
  w: number;
  field: string;
  sortField: string | null;
  sortDirection: ViewSortDirection;
  onSort: (field: string) => void;
  children: React.ReactNode;
  /**
   * RN-PROST (/work-order/6010) Column Manager only: when set, the header becomes
   * draggable and drops this key as `text/plain` for the ColumnManager panel's drop
   * zone to read. Omitted everywhere else (/os, /work-order/6020), so those tables
   * render identically to before this prop existed.
   */
  dragKey?: string;
  /** Plain, non-clickable header (no ↕). For read-only summaries whose row order is meaningful. */
  disabled?: boolean;
}

/**
 * Sortable column header for the OS stage tables (Размерување / Продуктивност /
 * Материјално). Those tables have no column manager (no drag/resize/columnWidths), so
 * the full `SortableHeader.tsx` (which requires all of that) doesn't fit — this is a
 * small `th` that keeps the same visual indicator convention (↑ / ↓ / ↕), just without
 * the drag-and-drop/resize machinery.
 *
 * Active indicator is blue (CLAUDE.md accent rule), not SortableHeader's plain gray.
 */
const TableSortTh: React.FC<TableSortThProps> = ({ w, field, sortField, sortDirection, onSort, children, dragKey, disabled = false }) => {
  const active = sortField === field;
  if (disabled) {
    return (
      <th
        className={`border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] h-[26px] px-1.5 align-middle${dragKey ? " cursor-grab" : ""}`}
        style={{ width: w }}
        draggable={!!dragKey}
        onDragStart={dragKey ? (e) => {
          e.dataTransfer.setData("text/plain", dragKey);
          e.dataTransfer.effectAllowed = "move";
        } : undefined}
      >
        {children}
      </th>
    );
  }
  return (
    <th
      className={`border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-center text-[11px] h-[26px] px-1.5 align-middle cursor-pointer select-none hover:bg-gray-300${dragKey ? " cursor-grab" : ""}`}
      style={{ width: w }}
      onClick={() => onSort(field)}
      draggable={!!dragKey}
      onDragStart={dragKey ? (e) => {
        e.dataTransfer.setData("text/plain", dragKey);
        e.dataTransfer.effectAllowed = "move";
      } : undefined}
    >
      <span className="inline-flex items-center justify-center gap-1 leading-tight">
        <span>{children}</span>
        <span className={active ? "text-blue-600" : "text-gray-400"}>
          {active ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </span>
    </th>
  );
};

export default TableSortTh;
